import { ButtonBase, Stack, Typography } from '@mui/material';
import LookupChip from '../../components/LookupChip';
import { PRIORITY_COLOR, STATUS_COLOR } from '../../components/constants';
import {
  formatIssueDateTime,
  getAssignedToDisplay,
  getCompanyNameLabel,
  getLocalizedLookupLabel,
  getLookupLabel,
  getReporterDisplayLabel,
  humanizeCompanyType,
} from '../../components/utils';

function issueNumber(original, props, { openIssueDetail } = {}) {
  return (
    <ButtonBase
      onClick={event => {
        event.stopPropagation();
        openIssueDetail?.(original);
      }}
      sx={{
        color: 'primary.main',
        font: 'inherit',
        fontWeight: 600,
        textAlign: 'left',
      }}>
      {original?.issueNumber || ''}
    </ButtonBase>
  );
}

function title(original) {
  return (
    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
      <Typography variant="body2" noWrap title={original?.title || ''}>
        {original?.title || ''}
      </Typography>
    </Stack>
  );
}

function reporter(original) {
  const label =
    original?.reporterDisplayName ||
    getReporterDisplayLabel(original?.reporter) ||
    '-';
  return (
    <Typography variant="body2" noWrap title={label}>
      {label}
    </Typography>
  );
}

function company(original) {
  const label =
    original?.companyDisplayName || getCompanyNameLabel(original?.company);
  return (
    <Typography variant="body2" noWrap title={label}>
      {label}
    </Typography>
  );
}

function reporterCompanyType(original, formatMessage) {
  const label =
    humanizeCompanyType(
      original?.reporterCompanyType || original?.company?.companyType,
      formatMessage,
    ) || '-';
  return (
    <Typography variant="body2" noWrap title={label}>
      {label}
    </Typography>
  );
}

function assignedTo(original) {
  const { organization, user } = getAssignedToDisplay(original);
  const primaryLabel = user || organization || '-';

  return (
    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
      <Typography variant="body2" noWrap title={primaryLabel}>
        {primaryLabel}
      </Typography>
      {user && organization && (
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          title={organization}>
          {organization}
        </Typography>
      )}
    </Stack>
  );
}

function lookupValue(original, fieldName) {
  const code = original?.[`${fieldName}Code`] || original?.[fieldName]?.code;
  const label =
    original?.[`${fieldName}Label`] ||
    (original?.[fieldName] ? getLookupLabel(original[fieldName]) : '') ||
    getLookupLabel(code);

  return { code, label };
}

function typeCode(original, formatMessage) {
  const lookup = original?.type || {
    code: original?.typeCode,
    label: original?.typeLabel,
  };
  return getLocalizedLookupLabel(lookup, 'TYPE', formatMessage);
}

function priorityCode(original, formatMessage) {
  const lookup = lookupValue(original, 'priority');
  return (
    <LookupChip
      lookup={{
        ...lookup,
        label: getLocalizedLookupLabel(lookup, 'PRIORITY', formatMessage),
      }}
      colorMap={PRIORITY_COLOR}
    />
  );
}

function statusCode(original, formatMessage) {
  const lookup = lookupValue(original, 'status');
  return (
    <LookupChip
      lookup={{
        ...lookup,
        label: getLocalizedLookupLabel(lookup, 'STATUS', formatMessage),
      }}
      colorMap={STATUS_COLOR}
    />
  );
}

function createdDateTime(original) {
  return formatIssueDateTime(original?.createdDateTime);
}

function lastModifiedDateTime(original) {
  return formatIssueDateTime(original?.lastModifiedDateTime);
}

export {
  assignedTo,
  company,
  createdDateTime,
  issueNumber,
  lastModifiedDateTime,
  priorityCode,
  reporter,
  reporterCompanyType,
  statusCode,
  title,
  typeCode,
};
